// Style controls for the current selection (or the pen's settings while
// drawing): sticky colour, shape fill/stroke, stroke colour/width, connector
// routing and arrowheads. Shown for selections of a single object type.
import type { BoardObject, Connector, Op } from '../../shared/types.ts';
import { STICKY_COLORS } from '../../shared/stress.ts';
import { CARD_COLORS } from '../../shared/cards.ts';
import { CARD_COLOR_NAMES } from '../cards/card-editor.ts';
import type { Editor } from '../editor.ts';
import { h } from '../util/dom.ts';
import { union } from '../util/geometry.ts';

const STROKES = ['#1A1A1A', '#5F6368', '#4262FF', '#D93025', '#188038', '#F76B15', '#8E4EC6'];
const FILLS = ['#FFFFFF', '#E8F0FE', '#FDECEA', '#E6F4EA', '#FEF7E0', '#F3E8FD', 'transparent'];
const WIDTHS: [number, string][] = [[2, 'Thin'], [4, 'Medium'], [8, 'Thick']];
const COLOR_NAMES: Record<string, string> = {
  '#1A1A1A': 'Black', '#5F6368': 'Grey', '#4262FF': 'Blue', '#D93025': 'Red', '#188038': 'Green', '#F76B15': 'Orange', '#8E4EC6': 'Purple',
  '#FFFFFF': 'White', '#E8F0FE': 'Light blue', '#FDECEA': 'Light red', '#E6F4EA': 'Light green', '#FEF7E0': 'Light yellow', '#F3E8FD': 'Light purple',
  ...CARD_COLOR_NAMES, transparent: 'No fill', '#FFE066': 'Yellow', '#FFB3C7': 'Pink', '#A8E6CF': 'Mint', '#9AD0F5': 'Sky', '#FFCB8E': 'Peach', '#D4B8FF': 'Lavender',
};

export function createContextBar(ed: Editor): HTMLElement {
  const bar = h('div', { class: 'panel context-bar', role: 'toolbar', 'aria-label': 'Style', hidden: '' });
  // Keep focus on the board (so shortcuts keep working) when clicking controls.
  bar.addEventListener('mousedown', (e) => e.preventDefault());

  const update = (ops: Op[]) => ed.apply(ops);
  const setAll = (objs: BoardObject[], props: Record<string, unknown>) =>
    update(objs.map((o) => ({ t: 'update', id: o.id, props: props as Partial<BoardObject> })));

  const swatches = (label: string, colors: readonly string[], current: string | null, pick: (c: string) => void) =>
    h('div', { class: 'cb-group', role: 'group', 'aria-label': label }, colors.map((c) => {
      const b = h('button', { type: 'button', class: c === 'transparent' ? 'swatch none' : 'swatch', title: COLOR_NAMES[c] ?? c, 'aria-label': `${label}: ${COLOR_NAMES[c] ?? c}`, 'aria-pressed': String(c === current) });
      if (c !== 'transparent') b.style.background = c;
      b.addEventListener('click', () => { pick(c); render(); });
      return b;
    }));

  const toggles = (label: string, items: [string, boolean, () => void][]) =>
    h('div', { class: 'cb-group', role: 'group', 'aria-label': label }, items.map(([text, on, act]) => {
      const b = h('button', { type: 'button', class: 'cb-toggle', 'aria-pressed': String(on) }, [text]);
      b.addEventListener('click', () => { act(); render(); });
      return b;
    }));

  const widths = (current: number | null, pick: (w: number) => void) =>
    toggles('Width', WIDTHS.map(([w, name]) => [name, w === current, () => pick(w)]));

  /** The shared value of `key` across objects, or null if they differ. */
  const common = <T>(objs: BoardObject[], key: string): T | null => {
    const values = new Set(objs.map((o) => (o as unknown as Record<string, unknown>)[key]));
    return values.size === 1 ? ([...values][0] as T) : null;
  };

  function controls(): Node[] | null {
    if (ed.readOnly || ed.transforming || ed.editingId) return null;
    if (ed.toolName === 'pen') {
      return [
        swatches('Pen colour', STROKES, ed.penColor, (c) => { ed.penColor = c; }),
        widths(ed.penWidth, (w) => { ed.penWidth = w; }),
      ];
    }
    if (ed.toolName !== 'select') return null;
    const objs = ed.selectedObjects();
    const type = common<string>(objs, 'type');
    if (!objs.length || !type) return null;
    switch (type) {
      case 'sticky':
        return [swatches('Colour', STICKY_COLORS, common(objs, 'color'), (c) => { ed.stickyColor = c; setAll(objs, { color: c }); })];
      case 'card': {
        const open = h('button', { type: 'button', class: 'cb-toggle', 'aria-pressed': 'false' }, ['Open']);
        open.addEventListener('click', () => ed.openCard(objs[0]!.id));
        const parts: Node[] = [swatches('Colour', CARD_COLORS, common(objs, 'color'), (c) => { ed.cardColor = c; setAll(objs, { color: c }); })];
        if (objs.length === 1) parts.push(h('div', { class: 'cb-group' }, [open]));
        return parts;
      }
      case 'shape':
        return [
          swatches('Fill', FILLS, common(objs, 'fill'), (c) => setAll(objs, { fill: c })),
          swatches('Border', STROKES, common(objs, 'stroke'), (c) => setAll(objs, { stroke: c })),
        ];
      case 'freehand':
        return [
          swatches('Colour', STROKES, common(objs, 'stroke'), (c) => setAll(objs, { stroke: c })),
          widths(common(objs, 'strokeWidth'), (w) => setAll(objs, { strokeWidth: w })),
        ];
      case 'connector': {
        const cs = objs as Connector[];
        const routing = common<string>(cs, 'routing');
        return [
          toggles('Line', [
            ['Straight', routing === 'straight', () => { ed.connectorRouting = 'straight'; setAll(cs, { routing: 'straight' }); }],
            ['Elbow', routing === 'elbow', () => { ed.connectorRouting = 'elbow'; setAll(cs, { routing: 'elbow' }); }],
          ]),
          toggles('Arrows', [
            ['← Start', cs.every((c) => c.arrowStart), () => setAll(cs, { arrowStart: !cs.every((c) => c.arrowStart) })],
            ['End →', cs.every((c) => c.arrowEnd), () => setAll(cs, { arrowEnd: !cs.every((c) => c.arrowEnd) })],
          ]),
          swatches('Colour', STROKES, common(cs, 'stroke'), (c) => setAll(cs, { stroke: c })),
        ];
      }
      default:
        return null;
    }
  }

  function render(): void {
    const parts = controls();
    bar.hidden = !parts;
    if (!parts) return;
    bar.replaceChildren(...parts);
    position();
  }

  function position(): void {
    if (bar.hidden) return;
    const bw = bar.offsetWidth, bh = bar.offsetHeight;
    const vw = window.innerWidth;
    let x = (vw - bw) / 2, y = 72;
    const objs = ed.toolName === 'select' ? ed.selectedObjects() : [];
    const bounds = union(objs.map((o) => ed.boundsOf(o)));
    if (bounds) {
      const [left, top] = ed.camera.worldToScreen(bounds.x, bounds.y);
      const [right, bottom] = ed.camera.worldToScreen(bounds.x + bounds.w, bounds.y + bounds.h);
      x = (left + right) / 2 - bw / 2;
      y = top - bh - 44; // clear of the rotate handle
      if (y < 72) y = bottom + 16;
    }
    x = Math.min(Math.max(8, x), vw - bw - 8);
    y = Math.min(Math.max(8, y), window.innerHeight - bh - 8);
    bar.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }

  for (const ev of ['selection', 'selectionContent', 'tool', 'transforming', 'history'] as const) ed.events.on(ev, render);
  ed.events.on('camera', position);
  document.addEventListener('focusin', render);
  document.addEventListener('focusout', () => setTimeout(render, 0));
  return bar;
}
